/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    ICLSStatus,
    ISaasLicensingStatus,
    LicenseTierType,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { Subject, takeUntil } from 'rxjs';
import * as globalL10n from 'global-l10n';

import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';

import './licensing-page-header.component.less';
import { AlertType } from '../../licensing.types';
import { LicensingPageStore } from '../licensing-page.store';
import * as l10n from './licensing-page-header.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Set of ENTERPRISE_WITH_CLOUD_SERVICES and Enterprise license tier type used
 * to show promotional message.
 */
export const CLOUD_SERVICES_ENTERPRISE_LICENSE_SET = new Set([
    LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES,
    LicenseTierType.ENTERPRISE,
]);

/**
 * Enum for licensing tab actions.
 */
enum LicensingTabActions {
    OVERVIEW = 'overview',
    TENANT_USAGE = 'tenant-usage',
}

/**
 * Constant for licensing tab actions.
 */
const LICENSING_TAB_ACTIONS = {
    OVERVIEW: LicensingTabActions.OVERVIEW,
    TENANT_USAGE: LicensingTabActions.TENANT_USAGE,
};

/**
 * @description
 *      Component to trigger the license tier change events
 *      and to open license tier comparison document tab.
 *
 * @author Rajawant Prajapati, Deepak Chaudhary, Shubam Bindal
 */
@Component({
    selector: 'licensing-page-header',
    templateUrl: './licensing-page-header.component.html',
})
export class LicensingPageHeaderComponent implements OnDestroy {
    /**
     * ENTERPRISE_WITH_CLOUD_SERVICES license status object.
     */
    @Input()
    public enterpriseWithCloudServicesLicenseStatus: ISaasLicensingStatus;

    /**
     * Emit license tier change event.
     */
    @Output()
    public onLicenseTierChangeRequest = new EventEmitter<void>();

    /**
     * Emit tab selection event.
     */
    @Output()
    public onTabSelect = new EventEmitter<string>();

    /**
     * Default license tier.
     */
    public defaultLicenseTier: LicenseTierType;

    public readonly licensingTabActions = LICENSING_TAB_ACTIONS;

    public activeTab: LicensingTabActions = LICENSING_TAB_ACTIONS.OVERVIEW;

    public readonly saasStatus$ = this.licensingPageStore.saasStatus$;

    public readonly clsStatus$ = this.licensingPageStore.clsStatus$;

    public readonly loadingLicenseStatus$ = this.licensingPageStore.loadingLicenseStatus$;

    /**
     * Use to display message on alert to unlock more features.
     */
    public licenseInfoAlertMessage: string;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        l10nService: L10nService,
        private readonly licensingPageStore: LicensingPageStore,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.licenseInfoAlertMessage = l10nService.getMessage(l10nKeys.unlockMoreFeaturesMessage);

        this.licensingPageStore.tierInUse$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((tierInUse: LicenseTierType) => {
                this.defaultLicenseTier = tierInUse;
            });
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Return true if ENTERPRISE_WITH_CLOUD_SERVICES tier is selected
     */
    public get isEnterpriseWithCloudServicesTier(): boolean {
        return this.defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;
    }

    /**
     * Function to emit license tier change event or open registration modal.
     */
    public onCogClick(): void {
        this.onLicenseTierChangeRequest.emit();
    }

    /**
     * Handle tab selection.
     */
    public tabSelect(tab: LicensingTabActions): void {
        this.activeTab = tab;
        this.onTabSelect.emit(tab);
    }

    /**
     * Return true when license tier type is neither of Enterprise or ENTERPRISE_WITH_CLOUD_SERVICES type.
     * When true, Show unlock more feature promotional message.
     */
    public get showLicenseInfoAlertMessage(): boolean {
        return !CLOUD_SERVICES_ENTERPRISE_LICENSE_SET.has(this.defaultLicenseTier);
    }

    public getLicenseStatusAlertType(
        saasStatus: ISaasLicensingStatus | undefined,
        clsStatus: ICLSStatus | undefined,
        loadingLicenseStatus: boolean,
    ): string {
        // Show grey (default) when loading
        if (loadingLicenseStatus) {
            return AlertType.default;
        }

        if (this.isEnterpriseWithCloudServicesTier) {
            // For ENTERPRISE_WITH_CLOUD_SERVICES, check saas_status
            return saasStatus?.enabled === true ? AlertType.success : AlertType.danger;
        } else {
            // For ENTERPRISE (on-prem), check cls_status
            return clsStatus?.enabled ? AlertType.success : AlertType.danger;
        }
    }

    /**
     * Retrieve the appropriate status message to display in the license status tooltip.
     */
    public getLicenseStatusAlertMessage(
        saasStatus: ISaasLicensingStatus | undefined,
        clsStatus: ICLSStatus | undefined,
    ): string {
        return (this.isEnterpriseWithCloudServicesTier ? saasStatus?.message : clsStatus?.message) ?? '';
    }
}
